import assertNever from "assert-never";
import { LocalDateTime } from "@js-joda/core";
import React from "react";
import {
  OnboardingVisitInstanceId,
  PublicOnboardingVisitDetailsTypes,
  PublicVisitBroadcastDetailsTypes,
  VisitInstanceId,
} from "../messages";
import { Visit } from "../system/AppState/AppVisitsState";
import { HashMap } from "../utils/HashMap";
import { HashSet } from "../utils/HashSet";
import { CheckedVisitData } from "./AvailableVisitActivity";
import { CheckedOnboardingVisitData } from "./OnboardingVisitActivity";

interface MultishiftPropsByType {
  OnboardingVisit: {
    visit: Visit.Onboarding;
    visitInstanceId: OnboardingVisitInstanceId;
    checkedData: CheckedOnboardingVisitData;
  };
  AvailableVisit: {
    visit: Visit.Available;
    visitInstanceId: VisitInstanceId;
    checkedData: CheckedVisitData;
  };
}
interface Props<MultishiftPropsType extends keyof MultishiftPropsByType> {
  visit: MultishiftPropsByType[MultishiftPropsType]["visit"];
  onClickRequest: (
    checkedVisits: HashSet<MultishiftPropsByType[MultishiftPropsType]["visitInstanceId"]>,
    checkedPatterns: HashSet<string>
  ) => void;
  onClickCancel: (
    visitInstances: MultishiftPropsByType[MultishiftPropsType]["visitInstanceId"][]
  ) => void;
  children: (props: {
    onClickRequest: () => void;
    onClickCancel: () => void;
    checkedVisits: HashSet<MultishiftPropsByType[MultishiftPropsType]["visitInstanceId"]>;
    checkedPatterns: HashSet<string>;
    areAllShiftsChecked: boolean;
    onCheckVisit: (visitData: MultishiftPropsByType[MultishiftPropsType]["checkedData"]) => void;
  }) => React.ReactNode;
}

export default function WithMultiShift<MultishiftPropsType extends keyof MultishiftPropsByType>(
  props: Props<MultishiftPropsType>
) {
  const availableVisits = HashSet.fromCollection<
    MultishiftPropsByType[MultishiftPropsType]["visitInstanceId"]
  >(getVisitInstanceIds(props.visit));
  const requestedInstances = getRequestedVisitInstances(props.visit);
  const [requestedVisits, setRequestedVisits] = React.useState<
    HashSet<MultishiftPropsByType[MultishiftPropsType]["visitInstanceId"]>
  >(() => {
    if (availableVisits.size() === 1) {
      return HashSet.fromCollection(availableVisits.values());
    }
    if (requestedInstances === null) {
      return HashSet.empty();
    }
    return requestedInstances.length > 0
      ? HashSet.fromCollection(requestedInstances)
      : HashSet.empty();
  });
  const [requestedPatterns, setRequestedPatterns] = React.useState<HashSet<string>>(() => {
    if (requestedInstances === null || props.visit.publicVisitBroadcastDetails.type !== "Pattern") {
      return HashSet.empty();
    }
    const availablePatternsMapResult = HashMap.fromArray(
      groupVisitsByDayOfWeek(props.visit.publicVisitBroadcastDetails)
    );
    if (availablePatternsMapResult.size() === 1) {
      setRequestedVisits(HashSet.fromCollection(availableVisits.values()));
      return HashSet.fromCollection([availablePatternsMapResult.keys().next().value]);
    } else {
      return HashSet.fromCollection(
        getRequestedPatternsFromBroadcast(requestedInstances, availablePatternsMapResult)
      );
    }
  });
  const patternVisitsMap: HashMap<
    string,
    MultishiftPropsByType[MultishiftPropsType]["visitInstanceId"][]
  > =
    availableVisits.size() === 0 || props.visit.publicVisitBroadcastDetails.type !== "Pattern"
      ? HashMap.empty()
      : HashMap.fromArray(groupVisitsByDayOfWeek(props.visit.publicVisitBroadcastDetails));

  const [areAllShiftsChecked, setAreAllShiftsChecked] = React.useState(() => {
    if (requestedInstances === null || requestedInstances.length === 0) {
      return false;
    }
    return requestedInstances.length === availableVisits.size();
  });

  const handleCheckVisit = (
    visitData: MultishiftPropsByType[MultishiftPropsType]["checkedData"]
  ) => {
    const requestedVisitsCount = requestedVisits.size();
    switch (visitData.type) {
      case "Singles":
        if (requestedVisits.has(visitData.visitInstanceId)) {
          setRequestedVisits(requestedVisits.delete(visitData.visitInstanceId));
          setAreAllShiftsChecked(false);
        } else {
          setRequestedVisits(requestedVisits.add(visitData.visitInstanceId));
          if (requestedVisitsCount + 1 === availableVisits.size()) {
            setAreAllShiftsChecked(true);
          }
        }
        break;
      case "Pattern": {
        const visitInstances = patternVisitsMap.get(visitData.dayOfWeek);
        const tempVisitInstances = new Set<
          MultishiftPropsByType[MultishiftPropsType]["visitInstanceId"]
        >(requestedVisits.values());

        if (visitInstances === undefined) {
          return;
        }

        if (requestedPatterns.has(visitData.dayOfWeek)) {
          setRequestedPatterns(requestedPatterns.delete(visitData.dayOfWeek));
          visitInstances.forEach((visitInstanceId) => {
            tempVisitInstances.delete(visitInstanceId);
          });
          setAreAllShiftsChecked(false);
        } else {
          setRequestedPatterns(requestedPatterns.add(visitData.dayOfWeek));
          visitInstances.forEach((visitInstanceId) => {
            tempVisitInstances.add(visitInstanceId);
          });
        }

        setRequestedVisits(HashSet.fromCollection(tempVisitInstances));

        if (tempVisitInstances.size === availableVisits.size()) {
          setAreAllShiftsChecked(true);
        }
        break;
      }
      case "AllShifts":
        checkAllShifts();
        break;
      default:
        assertNever(visitData);
    }
  };

  const checkAllShifts = () => {
    if (!areAllShiftsChecked) {
      setRequestedVisits(availableVisits);
      if (patternVisitsMap.size() > 0) {
        setRequestedPatterns(HashSet.fromCollection(patternVisitsMap.keys()));
      }
    } else {
      setRequestedVisits(HashSet.empty());
      setRequestedPatterns(HashSet.empty());
    }
    setAreAllShiftsChecked(!areAllShiftsChecked);
  };

  const handleRequest = () => {
    props.onClickRequest(requestedVisits, requestedPatterns);
  };

  const handleCancel = () => {
    props.onClickCancel(Array.from(requestedVisits.values()));
  };

  function getRequestedVisitInstances(
    visit: MultishiftPropsByType[MultishiftPropsType]["visit"]
  ): MultishiftPropsByType[MultishiftPropsType]["visitInstanceId"][] | null {
    return visit.caregiverVisitEngagementDetails.requestedInstances;
  }

  function getVisitInstanceIds(
    visit: MultishiftPropsByType[MultishiftPropsType]["visit"]
  ): MultishiftPropsByType[MultishiftPropsType]["visitInstanceId"][] {
    if (visit.publicVisitBroadcastDetails.type === "FlexiblePattern") {
      return [];
    }

    return visit.publicVisitBroadcastDetails.visitInstances.map(
      (visitInstance) => visitInstance.visitInstanceId
    );
  }

  function groupVisitsByDayOfWeek(
    visits: PublicVisitBroadcastDetailsTypes.Pattern | PublicOnboardingVisitDetailsTypes.Pattern
  ): [string, MultishiftPropsByType[MultishiftPropsType]["visitInstanceId"][]][] {
    const resultMap = new Map<
      string,
      MultishiftPropsByType[MultishiftPropsType]["visitInstanceId"][]
    >();

    for (const visitInstance of visits.visitInstances) {
      const dayOfWeek = LocalDateTime.parse(visitInstance.date.startDateTime.toString())
        .dayOfWeek()
        .name();
      if (!resultMap.has(dayOfWeek)) {
        resultMap.set(dayOfWeek, [visitInstance.visitInstanceId]);
      } else {
        const visitsOfDayOfWeek = resultMap.get(dayOfWeek);
        if (visitsOfDayOfWeek !== undefined) {
          resultMap.set(dayOfWeek, [...visitsOfDayOfWeek, visitInstance.visitInstanceId]);
        }
      }
    }

    const resultArray = Array.from(resultMap.entries());
    return resultArray;
  }

  function getRequestedPatternsFromBroadcast(
    requestedVisitInstances: MultishiftPropsByType[MultishiftPropsType]["visitInstanceId"][],
    patternVisitsDict: HashMap<
      string,
      MultishiftPropsByType[MultishiftPropsType]["visitInstanceId"][]
    >
  ): string[] {
    const resultPatterns = [];
    for (const dayOfWeek of patternVisitsDict.keys()) {
      const visitInstances = patternVisitsDict.get(dayOfWeek);
      let allVisitsForThisDayAreRequested = true;
      if (visitInstances !== undefined) {
        for (const visitInstanceId of visitInstances) {
          if (!requestedVisitInstances.includes(visitInstanceId)) {
            allVisitsForThisDayAreRequested = false;
            break;
          }
        }
        if (allVisitsForThisDayAreRequested) {
          resultPatterns.push(dayOfWeek);
        }
      }
    }
    return resultPatterns;
  }

  return (
    <React.Fragment>
      {props.children({
        onClickRequest: handleRequest,
        onClickCancel: handleCancel,
        checkedPatterns: requestedPatterns,
        areAllShiftsChecked: areAllShiftsChecked,
        onCheckVisit: handleCheckVisit,
        checkedVisits: requestedVisits,
      })}
    </React.Fragment>
  );
}
